import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FaPlus,
  FaExpand,
  FaCog,
  FaTrash,
  FaEdit,
  FaSync,
  FaArrowsAlt,
  FaCompress
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import WidgetBuilder from './HomeWidgetBuilder';
import ChartDisplay from '../reports/charts/ChartDisplay';
import ReportViewer from '../reports/ReportViewer';
import DashboardCanvas from '../dashboard/DashboardCanvas';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './css/HomeWidget.css';
import AppToast from '../AppToast';
import useReportChartData from '../../hooks/useReportChartData';
import jwt_decode from 'jwt-decode';

/** Effective CRM user id for row-level scope (home widgets are shared rows; cache must be viewer-specific). */
const getLoggedInUserIdFromToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = jwt_decode(token);
    return decoded?.id ?? null;
  } catch {
    return null;
  }
};

const HomeWidget = () => {
  const location = useLocation();

  // Determine mode based on route
  const isEditMode = location.pathname === '/homewidget';
  const isRunMode = location.pathname === '/';

  const [widgets, setWidgets] = useState([]);
  const [originalWidgets, setOriginalWidgets] = useState([]); // Track original widgets for deletion detection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [widgetChartData, setWidgetChartData] = useState({});
  const [loadingWidgets, setLoadingWidgets] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewingWidget, setViewingWidget] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [draggedWidget, setDraggedWidget] = useState(null); // Still needed for resize functionality
  const [resizeStartPosition, setResizeStartPosition] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const rowSpanOptions = Array.from({ length: 25 }, (_, index) => index + 1);
  const { loadReportChartData } = useReportChartData();
  const isEditingDashboardWidget = (editFormData.type || '').toLowerCase() === 'dashboard';

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', variant: 'success' });
  };

  // Load home widgets
  const loadHomeWidgets = useCallback(async () => {
    try {
      const homeWidgetApi = new DataApi('homewidgets');
      const response = await homeWidgetApi.get('/');
      console.log('HomeWidget - Response:', response);

      // Handle the actual API response structure - data is directly in response.data
      let widgetsData = [];
      if (response.data && Array.isArray(response.data)) {
        widgetsData = response.data;
      } else if (response.data && response.data.success && response.data.data) {
        // Fallback for success wrapper format
        widgetsData = response.data.data;
      }

      console.log('HomeWidget - Loaded widgets:', widgetsData);

      // Filter out deleted widgets
      const validWidgets = widgetsData.filter(widget => {
        return widget && widget.id && !widget.is_deleted;
      });

      console.log('HomeWidget - Valid widgets:', validWidgets.length, 'out of', widgetsData.length);

      setWidgets(validWidgets);
      setOriginalWidgets(validWidgets);

      // Load chart data for chart widgets only
      const chartWidgets = validWidgets.filter(widget => {
        const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
        return (widgetType === 'task' || widgetType === 'chart') && widget.parent_id;
      });
      if (chartWidgets.length > 0) {
        loadWidgetChartData(chartWidgets);
      }
    } catch (err) {
      console.error('Error loading widgets:', err);
      // Don't show error on first load - just set empty widgets
      setWidgets([]);
      setOriginalWidgets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Delay initial load to allow splash screen to complete
    const timer = setTimeout(() => {
      loadHomeWidgets();
    }, 100);

    return () => clearTimeout(timer);
  }, [loadHomeWidgets]);

  useEffect(() => {
    if (!viewingWidget) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow || '';
    };
  }, [viewingWidget]);

  // Helper function to transform dataset to previewData array format
  const transformDatasetToPreviewData = (dataset) => {
    if (!dataset || !dataset._metadata) {
      return [];
    }

    const metadata = dataset._metadata;
    const groupFields = metadata.group_fields || {};
    const summarizedFields = metadata.summarized_fields || {};

    // Get the primary group field (first group field)
    const primaryGroupFieldName = Object.keys(groupFields)[0];
    if (!primaryGroupFieldName) {
      return [];
    }

    const primaryGroupData = dataset[primaryGroupFieldName];

    if (!primaryGroupData) {
      return [];
    }

    // Transform the nested data structure to flat array format
    const previewData = [];

    Object.keys(primaryGroupData).forEach(groupValue => {
      const groupData = primaryGroupData[groupValue];

      // Create a row object for this group value
      const row = {
        [primaryGroupFieldName]: groupValue
      };

      // Add summarized field values
      Object.keys(summarizedFields).forEach(fieldName => {
        const fieldData = groupData[fieldName];
        if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
          row[fieldName] = fieldData.value;
        } else if (fieldData !== undefined) {
          row[fieldName] = fieldData;
        }
      });

      previewData.push(row);
    });

    // Handle multiple group fields case (like the second widget with name and first_name)
    const groupFieldNames = Object.keys(groupFields);
    if (groupFieldNames.length > 1) {
      // For multiple group fields, we need to create additional rows
      // This is a simplified approach - you might need to adjust based on your specific needs
      const additionalRows = [];

      groupFieldNames.slice(1).forEach(groupFieldName => {
        const groupData = dataset[groupFieldName];
        if (groupData) {
          Object.keys(groupData).forEach(groupValue => {
            const fieldData = groupData[groupValue];
            const row = {
              [groupFieldName]: groupValue
            };

            // Add summarized field values
            Object.keys(summarizedFields).forEach(fieldName => {
              const fieldValue = fieldData[fieldName];
              if (fieldValue && typeof fieldValue === 'object' && fieldValue.value !== undefined) {
                row[fieldName] = fieldValue.value;
              } else if (fieldValue !== undefined) {
                row[fieldName] = fieldValue;
              }
            });

            additionalRows.push(row);
          });
        }
      });

      previewData.push(...additionalRows);
    }

    return previewData;
  };

  /**
   * Home chart/task widgets: always load from the report API for the logged-in user (JWT).
   * We do not use shared DB dataset cache — same permission model as the report viewer, no dashboard-style cache.
   */
  const loadWidgetChartData = async (widgetsData) => {
    console.log('HomeWidget - Loading chart data (always fetch) for widgets:', widgetsData);

    for (const widget of widgetsData) {
      const reportIdForWidget = widget.parent_id || widget.report_id;
      const chartConfigSource = widget.chart_config || widget.selectedChart || null;
      const chartId = chartConfigSource?.id || widget.chart_id;

      console.log('HomeWidget - Processing widget:', {
        id: widget.id,
        name: widget.name,
        effective_report_id: reportIdForWidget,
        chart_id: chartId
      });

      if (!reportIdForWidget || !chartId) {
        console.log('HomeWidget - Missing report or chart id, skipping:', widget.id);
        continue;
      }

      await loadChartDataForWidget(widget);
    }
  };

  const loadChartDataForWidget = async (widget, options = {}) => {
    if (!widget) {
      console.warn('HomeWidget - Widget is undefined, cannot load chart data');
      return;
    }

    const reportId = widget.parent_id || widget.report_id;
    const chartConfigSource = widget.chart_config || widget.selectedChart || widgetChartData[widget.id]?.chartConfig;
    const chartId = chartConfigSource?.id || widget.chart_id;

    if (!reportId) {
      console.warn('HomeWidget - Missing report for widget:', {
        widgetId: widget.id,
        reportId
      });
      return;
    }

    try {
      console.log('HomeWidget - Loading chart data for widget:', widget.id);
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      const runningUserId = getLoggedInUserIdFromToken() || undefined;

      const result = await loadReportChartData({
        reportId,
        chartId: chartId || undefined,
        existingChartConfig: chartConfigSource,
        limit: 100000,
        runningUserId,
        ...options
      });

      const dataset = result.dataset || {};
      const previewData = result.previewData || result.rawData || [];
      const chartConfig = result.chartConfig || chartConfigSource;

      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData,
          summarizedData: dataset,
          groupingInfo: result.groupingInfo,
          selectedFields: result.selectedFields || [],
          summarizedFields: result.summarizedFields || {},
          selectedGroups: result.selectedGroups || [],
          chartConfig
        }
      }));

      setWidgets(prev => prev.map(w =>
        w.id === widget.id
          ? {
            ...w,
            dataset,
            summarizedData: dataset,
            selectedFields: result.selectedFields || [],
            summarizedFields: result.summarizedFields || {},
            selectedGroups: result.selectedGroups || [],
            chart_config: chartConfig
          }
          : w
      ));
    } catch (error) {
      console.error(`HomeWidget - Error loading chart data for widget ${widget.id}:`, error);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: false }));
    }
  };

  const handleDeleteWidget = (widget) => {
    setWidgetToDelete(widget);
    setShowDeleteModal(true);
  };

  const confirmDeleteWidget = async () => {
    if (!widgetToDelete) return;

    try {
      const homeWidgetApi = new DataApi('homewidgets');
      const response = await homeWidgetApi.delete(`/${widgetToDelete.id}`);

      if (response.data && response.data.success) {
        loadHomeWidgets(); // Reload the list
      } else {
        setError('Failed to delete widget');
        showToast('Failed to delete widget. Please try again.', 'danger');
      }
    } catch (err) {
      console.error('Error deleting widget:', err);
      const errorMessage = err?.response?.data?.message || 'Error deleting widget. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'danger');
    } finally {
      setWidgetToDelete(null);
      setShowDeleteModal(false);
    }
  };


  const handleAddWidget = async (newWidget) => {
    console.log('DashboardCanvas - Received widget:', newWidget);
    console.log('DashboardCanvas - Widget column_span:', newWidget.column_span);

    // Set default column span to 6 (half of 12-column grid) and row_span to 6 for extra large charts
    const widgetWithDefaults = {
      ...newWidget,
      column_span: newWidget.column_span || 6,
      row_span: newWidget.row_span || 6
    };

    console.log('DashboardCanvas - Final widget column_span:', widgetWithDefaults.column_span);
    console.log('DashboardCanvas - Final widget data:', widgetWithDefaults);

    // Add the new widget to the list (now it's a preview)
    setWidgets(prev => [...prev, widgetWithDefaults]);
    setShowSidebar(false);

    const reportId = widgetWithDefaults.parent_id || widgetWithDefaults.report_id;
    // WidgetBuilder sets type to 'chart' for report charts; chart_type is the visual type (bar, line, …)
    const widgetType = (widgetWithDefaults.type || widgetWithDefaults.widget_type || '').toLowerCase();

    if (reportId && widgetType === 'chart') {
      await loadChartDataForWidget(widgetWithDefaults);
    }
  };

  // Resize handlers
  const handleMouseDown = (e, widget, action) => {
    e.preventDefault();
    e.stopPropagation();

    if (action === 'resize') {
      // Get the handle from the current target (the resize handle div)
      const handle = e.currentTarget.dataset.handle || 'bottom-right';

      console.log('DashboardCanvas - Resize started:', {
        widgetId: widget.id,
        handle: handle,
        currentColumnSpan: widget.column_span,
        currentRowSpan: widget.row_span
      });
      setIsResizing(true);
      setResizeHandle(handle);
      setDraggedWidget(widget);
      setResizeStartPosition({ x: e.clientX, y: e.clientY });

      // Add visual feedback
      document.body.style.cursor = getResizeCursor(handle);
      document.body.style.userSelect = 'none';

      // Add event listeners immediately
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleMouseMove = useCallback((e) => {
    // Debug: Log mouse move events during resize
    if (isResizing) {
      console.log('DashboardCanvas - Mouse move during resize:', {
        isResizing: isResizing,
        draggedWidget: draggedWidget?.id,
        resizeHandle: resizeHandle
      });
    }

    if (isResizing && draggedWidget && resizeHandle) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const canvasWidth = canvasRect.width;
      const gridCellWidth = (canvasWidth - (11 * 20)) / 12;
      const gridRowHeight = 120; // Fixed row height - matches CSS grid-auto-rows

      let newColumnSpan = draggedWidget.column_span;
      let newRowSpan = draggedWidget.row_span;

      console.log('DashboardCanvas - Resize in progress:', {
        widgetId: draggedWidget.id,
        handle: resizeHandle,
        mouseX: e.clientX,
        mouseY: e.clientY,
        canvasRect: canvasRect,
        gridCellWidth: gridCellWidth,
        currentColumnSpan: draggedWidget.column_span,
        currentRowSpan: draggedWidget.row_span
      });

      if (resizeHandle.includes('right')) {
        const currentX = draggedWidget.x || 0;
        const maxColumns = 12 - currentX;
        const mouseX = e.clientX - canvasRect.left;
        const widgetLeftX = currentX * (gridCellWidth + 20); // Include gap between columns
        const relativeX = mouseX - widgetLeftX;

        // Snap to grid with minimum 1 column
        newColumnSpan = Math.max(1, Math.min(maxColumns, Math.round(relativeX / (gridCellWidth + 20))));

        // Ensure we don't exceed grid boundaries
        if (currentX + newColumnSpan > 12) {
          newColumnSpan = 12 - currentX;
        }
      }

      if (resizeHandle.includes('bottom')) {
        // Calculate the change in mouse position from the start
        const deltaY = e.clientY - resizeStartPosition.y;
        const rowHeight = gridRowHeight + 20; // Include gap between rows

        // Calculate how many rows to add/subtract based on mouse movement
        const rowChange = Math.round(deltaY / rowHeight);

        // Apply the change to the original row span
        const originalRowSpan = draggedWidget.row_span || 4;
        newRowSpan = Math.max(1, Math.min(originalRowSpan + rowChange, 10));

        console.log('DashboardCanvas - Bottom resize calculation:', {
          deltaY,
          rowHeight,
          rowChange,
          originalRowSpan,
          finalRowSpan: newRowSpan
        });
      }

      // Update resize preview
      setResizePreview({ column_span: newColumnSpan, row_span: newRowSpan });

      console.log('DashboardCanvas - Resize calculation:', {
        widgetId: draggedWidget.id,
        handle: resizeHandle,
        newColumnSpan: newColumnSpan,
        newRowSpan: newRowSpan,
        oldColumnSpan: draggedWidget.column_span,
        oldRowSpan: draggedWidget.row_span
      });

      setWidgets(prev => prev.map(widget =>
        widget.id === draggedWidget.id
          ? { ...widget, column_span: newColumnSpan, row_span: newRowSpan }
          : widget
      ));
    }
  }, [isResizing, draggedWidget, resizeHandle, resizeStartPosition]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Resize ended:', {
        widgetId: draggedWidget?.id,
        finalColumnSpan: draggedWidget?.column_span,
        finalRowSpan: draggedWidget?.row_span
      });
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    setIsResizing(false);
    setDraggedWidget(null);
    setResizeHandle(null);
    setResizePreview(null);
    setResizeStartPosition({ x: 0, y: 0 });

    // Reset cursor and user selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing, draggedWidget]);

  // Helper function to get appropriate resize cursor
  const getResizeCursor = (handle) => {
    switch (handle) {
      case 'bottom-right':
        return 'se-resize';
      case 'bottom':
        return 's-resize';
      case 'right':
        return 'e-resize';
      default:
        return 'default';
    }
  };

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Adding mouse event listeners');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        console.log('DashboardCanvas - Removing mouse event listeners');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const handlePreviewWidget = (widgetConfig) => {
    // Handle widget preview/update
    console.log('DashboardCanvas - Adding/updating widget:', widgetConfig);
    console.log('DashboardCanvas - Chart configuration received:', {
      selectedFields: widgetConfig.selectedFields,
      summarizedFields: widgetConfig.summarizedFields,
      selectedGroups: widgetConfig.selectedGroups,
      previewData: widgetConfig.previewData,
      summarizedData: widgetConfig.summarizedData,
      selectedChart: widgetConfig.selectedChart,
      isPreview: widgetConfig.isPreview,
      id: widgetConfig.id
    });

    console.log('DashboardCanvas - Widget config details:', {
      hasPreviewData: !!widgetConfig.previewData,
      hasSummarizedData: !!widgetConfig.summarizedData,
      hasGroupingInfo: !!widgetConfig.groupingInfo,
      previewDataType: typeof widgetConfig.previewData,
      summarizedDataType: typeof widgetConfig.summarizedData,
      groupingInfoType: typeof widgetConfig.groupingInfo
    });

    if (widgetConfig.isPreview) {
      // Add new widget to the widgets state
      setWidgets(prev => [...prev, widgetConfig]);
    } else {
      // Update existing widget in the widgets state
      setWidgets(prev => prev.map(widget => {
        if (widget.id === widgetConfig.id) {
          // Preserve existing important properties if widgetConfig doesn't have them
          const preservedSummarizedData = widgetConfig.summarizedData || widget.summarizedData;
          const preservedDataset = widgetConfig.dataset || widget.dataset;
          const preservedChartId = widgetConfig.chart_id || widget.chart_id;
          const preservedChartConfig = widgetConfig.chart_config || widget.selectedChart || widget.chart_config;
          const preservedReportId = widgetConfig.parent_id || widget.parent_id;

          console.log('DashboardCanvas - Updating widget with preserved data:', {
            widgetId: widget.id,
            widgetName: widget.name,
            hasOriginalSummarizedData: !!widget.summarizedData,
            hasOriginalDataset: !!widget.dataset,
            hasOriginalChartId: !!widget.chart_id,
            hasOriginalChartConfig: !!widget.chart_config,
            hasConfigSummarizedData: !!widgetConfig.summarizedData,
            hasConfigDataset: !!widgetConfig.dataset,
            hasConfigChartId: !!widgetConfig.chart_id,
            hasConfigChartConfig: !!widgetConfig.selectedChart,
            hasPreservedSummarizedData: !!preservedSummarizedData,
            hasPreservedDataset: !!preservedDataset,
            hasPreservedChartId: !!preservedChartId,
            hasPreservedChartConfig: !!preservedChartConfig
          });

          return {
            ...widgetConfig,
            summarizedData: preservedSummarizedData, // Ensure summarizedData is preserved
            dataset: preservedDataset, // Ensure dataset is preserved
            chart_id: preservedChartId, // Ensure chart_id is preserved
            chart_config: preservedChartConfig, // Ensure chart_config is preserved
            parent_id: preservedReportId // Ensure parent_id is preserved
          };
        }
        return widget;
      }));
    }

    // Check if widget already has chart data (from preview)
    if (widgetConfig.previewData && widgetConfig.summarizedData && widgetConfig.groupingInfo) {
      console.log('DashboardCanvas - Using existing chart data for widget:', widgetConfig.id);
      setWidgetChartData(prev => ({
        ...prev,
        [widgetConfig.id]: {
          previewData: widgetConfig.previewData,
          summarizedData: widgetConfig.summarizedData,
          groupingInfo: widgetConfig.groupingInfo,
          // Include chart configuration
          selectedFields: widgetConfig.selectedFields || [],
          summarizedFields: widgetConfig.summarizedFields || {},
          selectedGroups: widgetConfig.selectedGroups || [],
          chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || null
        }
      }));
    } else {
      // Check if we already have chart data for this widget (preserve existing data)
      const existingChartData = widgetChartData[widgetConfig.id];
      if (existingChartData) {
        console.log('DashboardCanvas - Preserving existing chart data for updated widget:', widgetConfig.id);
        // Keep the existing chart data, just update the widget configuration
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            ...existingChartData,
            // Update any new configuration if provided
            selectedFields: widgetConfig.selectedFields || existingChartData.selectedFields,
            summarizedFields: widgetConfig.summarizedFields || existingChartData.summarizedFields,
            selectedGroups: widgetConfig.selectedGroups || existingChartData.selectedGroups,
            chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || existingChartData.chartConfig || null
          }
        }));
      } else if (widgetConfig.dataset) {
        // If widget has dataset from database, use it directly and standardize to summarizedData
        console.log('DashboardCanvas - Using dataset from database for updated widget:', widgetConfig.id);
        const metadata = widgetConfig.dataset._metadata || {};
        const groupFields = metadata.group_fields || {};
        const summarizedFields = metadata.summarized_fields || {};

        // Transform dataset to previewData array format
        const previewData = transformDatasetToPreviewData(widgetConfig.dataset);
        console.log('DashboardCanvas - Transformed previewData for updated widget:', previewData);

        // Use the transformed data
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            previewData: previewData,
            summarizedData: widgetConfig.dataset,
            groupingInfo: null,
            selectedFields: Object.keys(summarizedFields),
            summarizedFields: summarizedFields,
            selectedGroups: Object.keys(groupFields),
            chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || null
          }
        }));

        // Update the widget to have consistent summarizedData while preserving original dataset
        setWidgets(prev => prev.map(w =>
          w.id === widgetConfig.id ? { ...w, summarizedData: widgetConfig.dataset, dataset: widgetConfig.dataset } : w
        ));
      } else {
        // Load chart data for the widget from API only if no existing data
        console.log('DashboardCanvas - Loading chart data from API for widget:', widgetConfig.id);
        console.log('DashboardCanvas - Widget config for API load:', {
          id: widgetConfig.id,
          name: widgetConfig.name,
          parent_id: widgetConfig.parent_id,
          chart_type: widgetConfig.chart_type,
          hasPreviewData: !!widgetConfig.previewData,
          hasSummarizedData: !!widgetConfig.summarizedData,
          hasGroupingInfo: !!widgetConfig.groupingInfo
        });

        // Ensure we load chart data for the updated widget
        loadChartDataForWidget(widgetConfig);
      }
    }
  };

  const handleRemoveWidget = (widgetId) => {
    console.log('DashboardCanvas - Removing widget:', widgetId);

    // Remove widget from UI state
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));

    // Remove chart data for the widget
    setWidgetChartData(prev => {
      const newData = { ...prev };
      delete newData[widgetId];
      return newData;
    });
  };

  const handleEditWidget = (widget) => {
    console.log('HomeWidget - Editing widget:', widget);

    // Set up edit form data
    setEditFormData({
      id: widget.id,
      name: widget.name || '',
      column_span: widget.column_span || 6,
      row_span: widget.row_span || 4,
      sort_order: widget.sort_order || 0,
      show_header: widget.show_header !== undefined ? widget.show_header : true,
      type: (widget.type || widget.widget_type || '').toLowerCase()
    });

    // Open edit modal
    setShowEditModal(true);
  };

  const handleViewWidget = (widget) => {
    console.log('DashboardCanvas - Viewing widget:', widget);
    setViewingWidget(widget);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    if (!editFormData.name.trim()) {
      // alert('Widget name is required');
      console.error('Widget name is required');
      return;
    }

    // Update the widget in the local widgets array (not saved to database yet)
    setWidgets(prev => prev.map(widget =>
      widget.id === editFormData.id
        ? {
          ...widget,
          name: editFormData.name.trim(),
          column_span: parseInt(editFormData.column_span),
          row_span: parseInt(editFormData.row_span),
          sort_order: parseInt(editFormData.sort_order),
          show_header: ((widget.type || widget.widget_type || '').toLowerCase() === 'dashboard')
            ? editFormData.show_header
            : (widget.show_header !== undefined ? widget.show_header : true)
        }
        : widget
    ));

    // Close the modal
    setShowEditModal(false);
    setEditFormData({});

    console.log('HomeWidget - Widget updated locally (will be saved when user clicks main Save button):', editFormData);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditFormData({});
  };

  const handleCloseViewModal = () => {
    setViewingWidget(null);
  };

  // Refresh widget data from API
  const handleRefreshWidget = async (widget) => {
    try {
      console.log('DashboardCanvas - Refreshing widget data:', widget.id);
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      // Load fresh data from API
      await loadChartDataForWidget(widget);

      console.log('DashboardCanvas - Widget refreshed successfully:', widget.id);
    } catch (err) {
      console.error('Error refreshing widget:', err);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: false }));
    }
  };

  const handleSaveWidgets = async () => {
    try {
      setSaving(true);
      setError(null);

      // Detect deleted widgets (widgets that were in originalWidgets but not in current widgets)
      const currentWidgetIds = widgets.map(w => w.id).filter(id => id && !id.toString().startsWith('preview-'));
      const deletedWidgets = originalWidgets.filter(original =>
        original.id && !currentWidgetIds.includes(original.id)
      );

      console.log('DashboardCanvas - Widget deletion analysis:', {
        originalCount: originalWidgets.length,
        currentCount: widgets.length,
        deletedCount: deletedWidgets.length,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name }))
      });

      // Send ALL widgets (from database + newly created from WidgetBuilder)
      // Backend will handle CREATE vs UPDATE based on ID presence
      console.log('DashboardCanvas - Sending all widgets for upsert:', {
        total: widgets.length,
        new: widgets.filter(w => !w.id || w.id.toString().startsWith('preview-')).length,
        existing: widgets.filter(w => w.id && !w.id.toString().startsWith('preview-')).length,
        deleted: deletedWidgets.length
      });

      if (widgets.length === 0) {
        setError('No widgets to save');
        return;
      }

      const homeWidgetApi = new DataApi('homewidgets');

      // Prepare all widgets data for upsert
      const widgetsData = widgets.map(widget => {

        // Blank ID for new widgets (preview-xxx IDs), keep ID for existing widgets
        const widgetId = (!widget.id || widget.id.toString().startsWith('preview-')) ? null : widget.id;
        const normalizedType = (widget.type || widget.widget_type || '').toLowerCase();
        const resolvedType = normalizedType || (widget.chart_config ? 'chart' : (widget.parent_id ? 'dashboard' : 'task'));
        const isDashboardWidget = resolvedType === 'dashboard';

        const chartConfig = widget.chart_config || widget.selectedChart || null;

        const parentId = isDashboardWidget
          ? (widget.parent_id || widget.dashboard_id || null)
          : (widget.parent_id || widget.report_id || null);

        console.log('HomeWidget - Preparing widget for upsert:', {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          type: resolvedType,
          parent_id: parentId, // dashboard/report id depending on widget type
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          hasChartConfig: !!chartConfig,
          datasetPersisted: false
        });

        const originalType = (widget.type || widget.widget_type || resolvedType || '').toLowerCase();
        const shouldShowHeader = originalType === 'dashboard'
          ? (widget.show_header !== undefined ? widget.show_header : true)
          : true;

        return {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          type: resolvedType,
          parent_id: parentId,
          chart_config: chartConfig,
          dataset: null,
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          show_header: shouldShowHeader
        };
      });

      console.log('DashboardCanvas - Sending widgets for upsert:', {
        count: widgetsData.length,
        new: widgetsData.filter(w => w.id === null).length,
        existing: widgetsData.filter(w => w.id !== null).length
      });


      const response = await homeWidgetApi.post('/upsert', {
        widgets: widgetsData,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name })) // Send deleted widget IDs
      });

      if (!response.data.success) {
        throw new Error(`Failed to save widgets: ${response.data.message || 'Unknown error'}`);
      }

      // Update widgets with their final IDs
      const savedWidgets = response.data.data;
      const updatedWidgets = widgets.map(widget => {
        // Find matching saved widget by name and original ID
        const savedWidget = savedWidgets.find(saved =>
          (saved.original_id === widget.id || saved.temp_id === widget.id) ||
          (saved.name === widget.name && !widget.id.toString().startsWith('preview-'))
        );

        if (savedWidget) {
          return { ...widget, id: savedWidget.id, isPreview: false };
        }
        return widget;
      });

      setWidgets(updatedWidgets);
      setOriginalWidgets(updatedWidgets); // Update original widgets to reflect current state

      // Show success message
      const deletedCount = deletedWidgets.length;
      const savedCount = savedWidgets.length;
      if (savedCount === 0 && deletedCount === 0) {
        showToast('No changes to save.', 'info');
      } else {
        const message = deletedCount > 0
          ? `Saved ${savedCount} widget${savedCount === 1 ? '' : 's'} and deleted ${deletedCount}.`
          : `Saved ${savedCount} widget${savedCount === 1 ? '' : 's'}.`;
        showToast(message, 'success');
      }

    } catch (err) {
      console.error('Error saving widgets:', err);
      const errorMessage = err?.message || 'Error saving widgets. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'danger');
    } finally {
      setSaving(false);
    }
  };


  const getChartTypeLabel = (chartType) => {
    switch (chartType) {
      case 'bar':
        return 'Bar Chart';
      case 'line':
        return 'Line Chart';
      case 'pie':
        return 'Pie Chart';
      case 'area':
        return 'Area Chart';
      case 'table':
        return 'Table';
      default:
        return 'Chart';
    }
  };

  const transformChartForDisplay = (widget) => {
    console.log('DashboardCanvas - Transforming widget for chart display:', widget);
    if (!widget) return null;

    // Extract chart configuration from widget - use chart_config from the response data
    const chartConfig = widget.chart_config || widget.selectedChart || {};

    // Create chart object by merging widget properties with chart_config data
    const chart = {
      // Basic chart properties from widget
      id: widget.chart_id || widget.id,
      title: widget.name,
      chart_type: chartConfig.chartType || widget.chart_type,

      // Chart configuration from chart_config
      ...chartConfig,

      // Widget-specific properties
      chartType: chartConfig.chartType,

      // Scatter chart specific fields
      scatterXAxis: chartConfig.scatterXAxis,
      scatterYAxis: chartConfig.scatterYAxis,
      scatterGroupField: chartConfig.scatterGroupField,
      scatterXAxisLabel: chartConfig.scatterXAxisLabel,
      scatterYAxisLabel: chartConfig.scatterYAxisLabel,

      // Other chart types
      xAxisField: chartConfig.xAxisField,
      yAxisField: chartConfig.yAxisField,
      yAxisFields: chartConfig.yAxisFields,
      groupField: chartConfig.groupField,
      pieValueField: chartConfig.pieValueField,
      pieSliceField: chartConfig.pieSliceField,

      // Display properties
      legendPosition: chartConfig.legendPosition || 'top',
      showTooltips: chartConfig.showTooltips !== undefined ? chartConfig.showTooltips : true,
      showLabels: chartConfig.showLabels !== undefined ? chartConfig.showLabels : true,
      labelColor: chartConfig.labelColor,
      xAxisLabel: chartConfig.xAxisLabel,
      yAxisLabel: chartConfig.yAxisLabel,
      fieldColors: chartConfig.fieldColors,
      legendColors: chartConfig.legendColors,
      showValues: chartConfig.showValues,
      showPercentages: chartConfig.showPercentages,
      showTotal: chartConfig.showTotal,
      valueFieldType: chartConfig.valueFieldType
    };

    console.log('DashboardCanvas - Transformed chart from widget:', chart);
    return chart;
  };


  if (loading) {
    return (
      <div className="home-widget-dashboard-canvas">
        <div className="home-widget-canvas-loading">
          <div className="home-widget-loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-widget-dashboard-canvas">
      {/* Header */}
      {!isRunMode && (
        <div className="home-widget-canvas-header">
          <div className="home-widget-canvas-header-content">
            <div className="home-widget-canvas-title">
              <h1>
                {loading ? (
                  <span className="home-widget-loading-text">Loading...</span>
                ) : (
                  'Home Widgets'
                )}
              </h1>
              <p className="home-widget-canvas-description">
                {loading ? (
                  <span className="home-widget-loading-text">Loading widgets...</span>
                ) : (
                  <span>{isEditMode ? 'Edit Home Widgets' : 'Manage and view your home widgets'}</span>
                )}
              </p>
            </div>
            <div className="home-widget-canvas-actions">
              <button
                className="home-widget-canvas-action-btn home-widget-canvas-save-btn"
                onClick={handleSaveWidgets}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                className="home-widget-canvas-action-btn home-widget-canvas-add-btn"
                onClick={() => setShowSidebar(true)}
              >
                <FaPlus />
                Add Widget
              </button>
              <button
                className="home-widget-canvas-action-btn home-widget-canvas-settings-btn"
                title="Dashboard Settings"
              >
                <FaCog />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="home-widget-canvas-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Canvas Grid */}
      <div className="home-widget-canvas-grid-container">
        {widgets.length === 0 ? (
          <div className="home-widget-canvas-empty-state">
            <div className="home-widget-empty-state-content">
              <div className="home-widget-empty-icon">
                <FaPlus />
              </div>
              <h3>No widgets yet</h3>
              <p>{isRunMode ? 'This dashboard has no widgets to display' : 'Start building your dashboard by adding widgets'}</p>
              {!isRunMode && (
                <button
                  className="home-widget-canvas-add-btn"
                  onClick={() => setShowSidebar(true)}
                >
                  <FaPlus />
                  Add Your First Widget
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={`home-widget-canvas-grid ${isRunMode ? 'home-widget-run-mode' : ''}`} ref={canvasRef}>
            {widgets
              .sort((a, b) => {
                // Handle widgets without sort_order (put them at the end)
                const aOrder = a.sort_order || 999999;
                const bOrder = b.sort_order || 999999;

                // Sort by sort_order (ascending)
                return aOrder - bOrder;
              })
              .map((widget, index) => {
                const normalizedType = (widget.type || widget.widget_type || '').toLowerCase();
                const normalizedChartType = (widget.chart_type || widget.chart_config?.chartType || '').toLowerCase();
                const widgetStyleType = (normalizedChartType || normalizedType || 'default').toLowerCase();
                const isDashboardWidget = normalizedType === 'dashboard';
                const isReportWidget = normalizedType === 'report' || normalizedChartType === 'report';
                const widgetReportId = widget.parent_id;
                const loadingLabel = isDashboardWidget ? 'dashboard' : isReportWidget ? 'report' : 'chart';
                const runModeLinkLabel = isDashboardWidget
                  ? widget.dashboard_label || widget.name || 'Dashboard'
                  : isReportWidget
                    ? widget.report_label || widget.name || 'Report'
                    : widget.name || 'Widget';
                const runModeLinkHref = isDashboardWidget
                  ? `/dashboard/${widget.parent_id}/run`
                  : isReportWidget && widgetReportId
                    ? `/viewreport/${widgetReportId}?source=reports`
                    : null;

                const columnSpan = widget.column_span || 6;
                const rowSpan = widget.row_span || 4;
                const widgetChartConfig = widget.chart_config || widget.selectedChart || widgetChartData[widget.id]?.chartConfig;
                const supportsRefresh = !isDashboardWidget && !!widgetChartConfig;
                const showHeaderValue = isDashboardWidget
                  ? (widget.show_header !== undefined ? widget.show_header : true)
                  : true;
                console.log(`DashboardCanvas - Rendering widget ${widget.id} (position ${index + 1}) with sort_order: ${widget.sort_order || 'none'}, column_span: ${columnSpan}, row_span: ${rowSpan}`);
                console.log(`DashboardCanvas - Widget ${widget.id} grid styles:`, {
                  gridColumn: `span ${columnSpan}`,
                  gridRow: `span ${rowSpan}`,
                  gridColumnStart: widget.x || 'auto',
                  gridRowStart: widget.y || 'auto'
                });
                console.log(`DashboardCanvas - Widget ${widget.id} raw data:`, widget);

                return (
                  <div
                    key={widget.id}
                    className={`home-widget-canvas-widget widget-${widgetStyleType} ${isResizing && draggedWidget?.id === widget.id ? 'home-widget-resizing' : ''} ${isRunMode ? 'home-widget-run-mode' : ''}`}
                    style={{
                      gridColumn: `span ${columnSpan}`,
                      gridRow: `span ${rowSpan}`,
                      gridColumnStart: widget.x || 'auto',
                      gridRowStart: widget.y || 'auto',
                      '--column-span': columnSpan,
                      '--row-span': rowSpan
                    }}
                    data-column-span={columnSpan}
                    data-row-span={rowSpan}

                  >
                    {/* Widget Header - Hidden in run mode */}
                    {!isRunMode && (
                      <div className="home-widget-widget-header">
                        <div className="home-widget-widget-title-section">
                          <div className="home-widget-widget-title-info">
                            <h3 className="home-widget-widget-title">
                              {widget.name}
                              {widget.isPreview && (
                                <span className="home-widget-preview-badge">Preview</span>
                              )}

                            </h3>

                          </div>
                        </div>
                        <div className="home-widget-widget-actions">
                          <button
                            className="home-widget-widget-action-btn home-widget-widget-expand-btn"
                            onClick={() => handleViewWidget(widget)}
                            title="View Widget in Full Screen"
                          >
                            <FaExpand />
                          </button>
                          <button
                            className="home-widget-widget-action-btn home-widget-widget-refresh-btn"
                            onClick={() => handleRefreshWidget(widget)}
                            title="Refresh Widget Data"
                            disabled={loadingWidgets[widget.id]}
                          >
                            <FaSync className={loadingWidgets[widget.id] ? 'home-widget-spinning' : ''} />
                          </button>
                          <button
                            className="home-widget-widget-action-btn home-widget-widget-edit-btn"
                            onClick={() => handleEditWidget(widget)}
                            title="Edit Widget"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="home-widget-widget-action-btn home-widget-widget-delete-btn"
                            onClick={() => handleRemoveWidget(widget.id)}
                            title="Remove Widget"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Widget Content */}
                    <div className="home-widget-widget-content">
                      <div className="home-widget-widget-chart-container">
                        {isRunMode && (
                          <div className="home-widget-run-actions widget-actions">
                            <button
                              className="widget-action-btn widget-expand-btn"
                              onClick={() => handleViewWidget(widget)}
                              title="View Widget in Full Screen"
                            >
                              <FaExpand />
                            </button>
                            {supportsRefresh && (
                              <button
                                className="widget-action-btn widget-refresh-btn"
                                onClick={() => handleRefreshWidget(widget)}
                                title="Refresh Widget Data"
                                disabled={loadingWidgets[widget.id]}
                              >
                                <FaSync className={loadingWidgets[widget.id] ? 'spinning' : ''} />
                              </button>
                            )}
                          </div>
                        )}
                        {loadingWidgets[widget.id] ? (
                          <div className="home-widget-widget-loading">
                            <div className="home-widget-loading-spinner"></div>
                            <p>Loading {loadingLabel}...</p>
                          </div>
                        ) : isDashboardWidget && widget.parent_id ? (
                          <div className="home-widget-widget-dashboard">
                            <DashboardCanvas
                              isRunMode={true}
                              dashboardId={widget.parent_id}
                              showHeader={showHeaderValue}
                              embedded={true}
                            />
                          </div>
                        ) : isReportWidget && widgetReportId ? (
                          <div className="home-widget-widget-report-viewer">
                            <ReportViewer
                              embed={true}
                              reportId={widgetReportId}
                              onError={(message) => {
                                console.error('HomeWidget - ReportViewer error:', message);
                              }}
                            />
                          </div>
                        ) : widgetChartData[widget.id] ? (
                          <div className="home-widget-widget-chart">
                            {console.log('DashboardCanvas - ChartDisplay props for widget:', widget.id, {
                              selectedFields: widgetChartData[widget.id].selectedFields,
                              summarizedFields: widgetChartData[widget.id].summarizedFields,
                              selectedGroups: widgetChartData[widget.id].selectedGroups,
                              summarizedData: widgetChartData[widget.id].summarizedData,
                              previewData: widgetChartData[widget.id].previewData
                            })}
                            <ChartDisplay
                              chart={transformChartForDisplay(widget)}
                              selectedFields={widgetChartData[widget.id].selectedFields || []}
                              summarizedFields={widgetChartData[widget.id].summarizedFields || {}}
                              selectedGroups={widgetChartData[widget.id].selectedGroups || []}
                              summarizedData={widgetChartData[widget.id].summarizedData}
                              previewData={widgetChartData[widget.id].previewData}
                              isPreview={true}
                              displayAuditInfo={false}
                            />
                          </div>
                        ) : (
                          <div className="home-widget-widget-chart-placeholder">
                            {(widget.type === 'task' || widget.widget_type === 'task') ? (
                              <div className="home-widget-task-widget-placeholder">
                                <div className="home-widget-placeholder-icon">📊</div>
                                <h4>Task Widget</h4>
                                <p>This widget is configured but chart data is not yet available.</p>
                                <small>Chart configuration will be implemented soon.</small>
                              </div>
                            ) : (
                              <div className="home-widget-widget-chart">
                                <div className="chart-container">
                                  {widget.name ? (
                                    <div className="chart-title">{widget.name}</div>
                                  ) : null}
                                  <div
                                    className="chart-placeholder"
                                    style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}
                                  >
                                    No data available
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Widget Footer */}
                    <div className="home-widget-widget-footer">
                      <div className="home-widget-widget-meta">
                        {isDashboardWidget && (
                          isRunMode ? (
                            <span
                              className="home-widget-widget-dashboard home-widget-widget-dashboard-link"
                              onClick={() => window.open(`/dashboard/${widget.parent_id}/run`, '_blank')}
                              title="Click to open dashboard"
                            >
                              Dashboard: {widget.dashboard_label || widget.name || 'Unknown'}
                            </span>
                          ) : (
                            <span className="home-widget-widget-dashboard">
                              Dashboard: {widget.dashboard_label || widget.name || 'Unknown'}
                            </span>
                          )
                        )}
                        {!isDashboardWidget && isReportWidget && widgetReportId && (
                          isRunMode ? (
                            <span
                              className="home-widget-widget-report home-widget-widget-report-link"
                              onClick={() => window.open(`/viewreport/${widgetReportId}?source=reports`, '_blank')}
                              title="Click to open report"
                            >
                              Report: {widget.report_label || widget.name || 'Unknown'}
                            </span>
                          ) : (
                            <span className="home-widget-widget-report">Report: {widget.report_label || widget.name || 'Unknown'}</span>
                          )
                        )}
                        {!isDashboardWidget && !isReportWidget && widgetReportId && (normalizedType === 'chart' || normalizedType === 'task' || widgetChartConfig) && (
                          isRunMode ? (
                            <span
                              className="home-widget-widget-chart-link home-widget-widget-report-link"
                              onClick={() => window.open(`/viewreport/${widgetReportId}?source=reports`, '_blank')}
                              title="Click to open report"
                            >
                              Chart: {widget.report_label || widget.name || 'Unknown'}
                            </span>
                          ) : (
                            <span className="home-widget-widget-chart-link">Chart: {widget.report_label || widget.name || 'Unknown'}</span>
                          )
                        )}
                      </div>
                      {!isRunMode && (
                        <div className="home-widget-widget-resize-handles">
                          <div
                            className="home-widget-resize-handle home-widget-resize-se"
                            data-handle="bottom-right"
                            onMouseDown={(e) => {
                              // Get the handle from the current target (the resize handle div)
                              const handle = e.currentTarget.dataset.handle || 'bottom-right';
                              console.log('DashboardCanvas - Resize handle clicked:', handle);
                              handleMouseDown(e, widget, 'resize');
                            }}
                            onClick={(e) => {
                              console.log('DashboardCanvas - Resize handle onClick:', e.target.dataset.handle);
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            title="Resize both width and height"
                          >
                            <FaExpand />
                          </div>
                          <div
                            className="home-widget-resize-handle home-widget-resize-s"
                            data-handle="bottom"
                            onMouseDown={(e) => {
                              // Get the handle from the current target (the resize handle div)
                              const handle = e.currentTarget.dataset.handle || 'bottom-right';
                              console.log('DashboardCanvas - Resize handle clicked:', handle);
                              handleMouseDown(e, widget, 'resize');
                            }}
                            title="Resize height"
                          >
                            <FaArrowsAlt />
                          </div>
                          <div
                            className="home-widget-resize-handle home-widget-resize-e"
                            data-handle="right"
                            onMouseDown={(e) => {
                              // Get the handle from the current target (the resize handle div)
                              const handle = e.currentTarget.dataset.handle || 'bottom-right';
                              console.log('DashboardCanvas - Resize handle clicked:', handle);
                              handleMouseDown(e, widget, 'resize');
                            }}
                            title="Resize width"
                          >
                            <FaArrowsAlt />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Widget Builder */}
      {!isRunMode && (
        <WidgetBuilder
          isOpen={showSidebar}
          onClose={() => {
            setShowSidebar(false);
            setEditingWidget(null);
          }}
          onAddWidget={handleAddWidget}
          onPreviewWidget={handlePreviewWidget}
          editingWidget={editingWidget}
          existingWidgets={widgets}
        />
      )}

      {/* Widget View Modal */}
      {viewingWidget && (
        <div className="home-widget-widget-view-modal-overlay" onClick={handleCloseViewModal}>
          <div className="home-widget-widget-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="home-widget-widget-view-modal-header">
              <h3>{viewingWidget.name}</h3>
              <button className="home-widget-widget-view-modal-close" onClick={handleCloseViewModal}>
                ×
              </button>
            </div>
            <div className="home-widget-widget-view-modal-body">
              {(() => {
                const viewingNormalizedType = (viewingWidget.type || viewingWidget.widget_type || '').toLowerCase();
                const viewingNormalizedChartType = (viewingWidget.chart_type || viewingWidget.chart_config?.chartType || '').toLowerCase();
                const viewingIsDashboard = viewingNormalizedType === 'dashboard';
                const viewingIsReport = viewingNormalizedType === 'report' || viewingNormalizedChartType === 'report';
                const viewingReportId = viewingWidget.report_id || viewingWidget.parent_id;

                if (viewingIsDashboard && viewingWidget.parent_id) {
                  return (
                    <DashboardCanvas
                      isRunMode={true}
                      dashboardId={viewingWidget.parent_id}
                      showHeader={viewingWidget.show_header !== undefined ? viewingWidget.show_header : true}
                      embedded={true}
                    />
                  );
                }

                if (viewingIsReport && viewingReportId) {
                  return (
                    <ReportViewer
                      embed={true}
                      reportId={viewingReportId}
                      onError={(message) => {
                        console.error('HomeWidget - ReportViewer modal error:', message);
                      }}
                    />
                  );
                }

                if (widgetChartData[viewingWidget.id]) {
                  return (
                    <ChartDisplay
                      chart={transformChartForDisplay(viewingWidget)}
                      selectedFields={widgetChartData[viewingWidget.id].selectedFields || []}
                      summarizedFields={widgetChartData[viewingWidget.id].summarizedFields || {}}
                      selectedGroups={widgetChartData[viewingWidget.id].selectedGroups || []}
                      summarizedData={widgetChartData[viewingWidget.id].summarizedData}
                      previewData={widgetChartData[viewingWidget.id].previewData}
                      isPreview={true}
                      displayAuditInfo={true}
                    />
                  );
                }

                return (
                  <div className="home-widget-widget-view-loading">
                    <div className="home-widget-loading-spinner"></div>
                    <p>Loading chart data...</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Edit Widget Modal */}
      {showEditModal && (
        <div className="home-widget-widget-edit-modal-overlay" onClick={handleCancelEdit}>
          <div className="home-widget-widget-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="home-widget-widget-edit-modal-header">
              <h3>Edit Widget</h3>
              <button className="home-widget-widget-edit-modal-close" onClick={handleCancelEdit}>
                ×
              </button>
            </div>
            <div className="home-widget-widget-edit-modal-body">
              <div className="home-widget-edit-form">
                <div className="home-widget-form-group">
                  <label htmlFor="widget-name">Widget Name</label>
                  <input
                    type="text"
                    id="widget-name"
                    value={editFormData.name || ''}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    placeholder="Enter widget name"
                    className="home-widget-form-input"
                  />
                </div>

                <div className="home-widget-form-row">
                  <div className="home-widget-form-group">
                    <label htmlFor="column-span">Column Span</label>
                    <select
                      id="column-span"
                      value={editFormData.column_span || 6}
                      onChange={(e) => handleEditFormChange('column_span', parseInt(e.target.value))}
                      className="home-widget-form-select"
                    >
                      <option value={3}>3 columns (25%)</option>
                      <option value={4}>4 columns (33%)</option>
                      <option value={6}>6 columns (50%)</option>
                      <option value={8}>8 columns (67%)</option>
                      <option value={9}>9 columns (75%)</option>
                      <option value={12}>12 columns (100%)</option>
                    </select>
                  </div>

                  <div className="home-widget-form-group">
                    <label htmlFor="row-span">Row Span</label>
                    <select
                      id="row-span"
                      value={editFormData.row_span || 4}
                      onChange={(e) => handleEditFormChange('row_span', parseInt(e.target.value))}
                      className="home-widget-form-select"
                    >
                      {rowSpanOptions.map((value) => (
                        <option key={value} value={value}>
                          {value === 1 ? '1 Row' : `${value} Rows`}{value === 4 ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="home-widget-form-group">
                  <label htmlFor="sort-order">Sort Order</label>
                  <input
                    type="number"
                    id="sort-order"
                    value={editFormData.sort_order || 0}
                    onChange={(e) => handleEditFormChange('sort_order', parseInt(e.target.value))}
                    placeholder="Sort order (0 = first)"
                    className="home-widget-form-input"
                    min="0"
                  />
                </div>

                {isEditingDashboardWidget && (
                  <div className="home-widget-form-group">
                    <label className="home-widget-checkbox-label">
                      <input
                        type="checkbox"
                        checked={editFormData.show_header !== undefined ? editFormData.show_header : true}
                        onChange={(e) => handleEditFormChange('show_header', e.target.checked)}
                      />
                      <span className="home-widget-checkbox-text">Show Header</span>
                    </label>
                    <p className="home-widget-form-help-text">
                      Display the dashboard/component header with title and controls
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="home-widget-widget-edit-modal-footer">
              <button className="home-widget-btn home-widget-btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="home-widget-btn home-widget-btn-primary" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setWidgetToDelete(null);
        }}
        onConfirm={confirmDeleteWidget}
        title="Confirm Delete"
        message="Are you sure you want to delete this widget?"
        itemName={widgetToDelete?.name}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        onClose={hideToast}
      />
    </div>
  );
};

export default HomeWidget;
